<!-- ChartComponentFemale.vue -->
<template>
  <div
    id="chartdiv-female"
    style="block-size: 500px; inline-size: 100%;"
  />
</template>

<script setup>
import * as am5 from "@amcharts/amcharts5"
import am5themesAnimated from "@amcharts/amcharts5/themes/Animated"
import * as am5xy from "@amcharts/amcharts5/xy"
import { defineProps, onMounted, ref } from 'vue'

const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
})

const chartInstance = ref(null)

const initChart = data => {
  let root = am5.Root.new("chartdiv-female")

  // Disable amCharts logo
  root._logo.dispose()

  root.setThemes([am5themesAnimated.new(root)])

  let chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
      paddingBottom: 50,
      paddingTop: 60,
      paddingLeft: 0,
      paddingRight: 0,
    }),
  )

  let xRenderer = am5xy.AxisRendererX.new(root, {
    minorGridEnabled: true,
    minGridDistance: 60,
  })
  xRenderer.grid.template.set("visible", false)

  let xAxis = chart.xAxes.push(
    am5xy.CategoryAxis.new(root, {
      paddingTop: 40,
      categoryField: "name",
      renderer: xRenderer,
    }),
  )

  let yRenderer = am5xy.AxisRendererY.new(root, {})
  yRenderer.grid.template.set("strokeDasharray", [3])

  let yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      min: 0,
      renderer: yRenderer,
    }),
  )

  let series = chart.series.push(
    am5xy.ColumnSeries.new(root, {
      name: "Steps",
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: "steps",
      categoryXField: "name",
      sequencedInterpolation: true,
      calculateAggregates: true,
      maskBullets: false,
      tooltip: am5.Tooltip.new(root, {
        dy: -30,
        pointerOrientation: "vertical",
        labelText: "{valueY}",
      }),
    }),
  )

  series.columns.template.setAll({
    strokeOpacity: 0,
    cornerRadiusBR: 10,
    cornerRadiusTR: 10,
    cornerRadiusBL: 10,
    cornerRadiusTL: 10,
    maxWidth: 50,
    fillOpacity: 0.8,
  })

  series.columns.template.adapters.add("fill", (fill, target) => {
    let dataItem = target.dataItem
    if (dataItem) {
      return dataItem.dataContext.color
    }
    
    return fill
  })

  let currentlyHovered

  series.columns.template.events.on("pointerover", function (e) {
    handleHover(e.target.dataItem)
  })

  series.columns.template.events.on("pointerout", function (e) {
    handleOut()
  })

  function handleHover(dataItem) {
    if (dataItem && currentlyHovered != dataItem) {
      handleOut()
      currentlyHovered = dataItem
      let bullet = dataItem.bullets[0]
      bullet.animate({
        key: "locationY",
        to: 1,
        duration: 600,
        easing: am5.ease.out(am5.ease.cubic),
      })
    }
  }

  function handleOut() {
    if (currentlyHovered) {
      let bullet = currentlyHovered.bullets[0]
      bullet.animate({
        key: "locationY",
        to: 0,
        duration: 600,
        easing: am5.ease.out(am5.ease.cubic),
      })
    }
  }

  let circleTemplate = am5.Template.new({})

  series.bullets.push(function (root, series, dataItem) {
    let bulletContainer = am5.Container.new(root, {})
    let circle = bulletContainer.children.push(
      am5.Circle.new(
        root,
        {
          radius: 30,  // Adjust the size
          fillOpacity: 0,  // Ensure the fill is transparent
          strokeOpacity: 0,  // Ensure no stroke (border)
        },
        circleTemplate,
      ),
    )

    let maskCircle = bulletContainer.children.push(
      am5.Circle.new(root, { radius: 25, fillOpacity: 0, strokeOpacity: 0 }), 
    )

    let imageContainer = bulletContainer.children.push(
      am5.Container.new(root, {
        mask: maskCircle,
      }),
    )

    let image = imageContainer.children.push(
      am5.Picture.new(root, {
        templateField: "pictureSettings",
        centerX: am5.p50,
        centerY: am5.p50,
        width: 50, // Adjusted width for better fit
        height: 50, // Adjusted height for better fit
      }),
    )

    return am5.Bullet.new(root, {
      locationY: 0,
      sprite: bulletContainer,
    })
  })

  series.set("heatRules", [
    {
      dataField: "valueY",
      min: am5.color(0xe5dc36),
      max: am5.color(0x5faa46),
      target: series.columns.template,
      key: "fill",
    },
    {
      dataField: "valueY",
      min: am5.color(0xe5dc36),
      max: am5.color(0x5faa46),
      target: circleTemplate,
      key: "fill",
    },
  ])


  series.data.setAll(data)
  xAxis.data.setAll(data)

  let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}))
  cursor.lineX.set("visible", false)
  cursor.lineY.set("visible", false)

  cursor.events.on("cursormoved", function () {
    let dataItem = series.get("tooltip").dataItem
    if (dataItem) {
      handleHover(dataItem)
    } else {
      handleOut()
    }
  })

  series.appear()
  chart.appear(1000, 100)

  chartInstance.value = chart
}

watch(
  () => props.data,
  newData => {
    if (chartInstance.value) {
      const chart = chartInstance.value
      const series = chart.series.getIndex(0)  // Ensure the chart has at least one series

      series.data.setAll(newData)  // Update the series data
      // chart.invalidateData()  // Refresh the chart with new data
    }
  },
  { immediate: true },
)

onMounted(() => {
  initChart(props.data)
})
</script>
